import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    loadWeatherConditions,
    getWeatherConditions,
    getWeatherConstants,
    getWeatherEnvironment,
    getWeatherConditionsLoading,
    getWeatherConditionsError,
    getWeatherConditionOptions,
    getConditionLabel,
    getQualifierLabel,
    getValueLabel,
    getTimeframeLabel,
    getIsWeatherTypeTemperature,
    getHasWeatherConditions,
    getMaxNumberOfConditions,
    getInputAdornmentMap,
    getValidWeatherConditionMultipleOptionType,
    getValidWeatherConditionQualifier,
    getValidWeatherConditionType,
    getValidWeatherConditionTimeframe,
} from 'states/resources/weather-conditions';

/**
 * Hook to manage weather conditions data from API
 * Replaces the static imports from common/constants/weather-condition-targeting
 */
export const useWeatherConditionsData = (campaignId, providedEnvironment = null) => {
    const dispatch = useDispatch();

    // Use provided environment or get from Redux store (safer than window.CONFIG)
    const storeEnvironment = useSelector(getWeatherEnvironment);
    const environment = providedEnvironment || storeEnvironment;

    // Fetch data on mount or when campaignId changes
    useEffect(() => {
        if (campaignId) {
            dispatch(loadWeatherConditions(campaignId, environment));
        }
    }, [campaignId, environment, dispatch]);

    // Selectors
    const weatherConditions = useSelector(getWeatherConditions);
    const constants = useSelector(getWeatherConstants);
    const currentEnvironment = useSelector(getWeatherEnvironment);
    const loading = useSelector(getWeatherConditionsLoading);
    const error = useSelector(getWeatherConditionsError);

    // Utility function selectors
    const getConditionOptions = useSelector(getWeatherConditionOptions);
    const getCondLabelFn = useSelector(getConditionLabel);
    const getQualifierLabelFn = useSelector(getQualifierLabel);
    const getValueLabelFn = useSelector(getValueLabel);
    const getTimeframeLabelFn = useSelector(getTimeframeLabel);
    const isWeatherTypeTemperatureFn = useSelector(getIsWeatherTypeTemperature);
    const hasWeatherConditionsFn = useSelector(getHasWeatherConditions);
    const maxNumberOfConditions = useSelector(getMaxNumberOfConditions);
    const inputAdornmentMap = useSelector(getInputAdornmentMap);

    // Validation constants
    const validWeatherConditionMultipleOptionType = useSelector(
        getValidWeatherConditionMultipleOptionType
    );
    const validWeatherConditionQualifier = useSelector(getValidWeatherConditionQualifier);
    const validWeatherConditionType = useSelector(getValidWeatherConditionType);
    const validWeatherConditionTimeframe = useSelector(getValidWeatherConditionTimeframe);

    return {
        // Data
        weatherConditions,
        constants,
        environment: currentEnvironment,
        loading,
        error,

        // Utility functions (equivalent to the original constants)
        getWeatherConditionOptions: getConditionOptions,
        getConditionLabel: getCondLabelFn,
        getQualifierLabel: getQualifierLabelFn,
        getValueLabel: getValueLabelFn,
        getTimeframeLabel: getTimeframeLabelFn,
        isWeatherTypeTemperature: isWeatherTypeTemperatureFn,
        hasWeatherConditions: hasWeatherConditionsFn,
        maxNumberOfConditions,
        inputAdornmentMap,

        // Validation constants
        VALID_WEATHER_CONDITION_MULTIPLE_OPTION_TYPE: validWeatherConditionMultipleOptionType,
        VALID_WEATHER_CONDITION_QUALIFIER: validWeatherConditionQualifier,
        VALID_WEATHER_CONDITION_TYPE: validWeatherConditionType,
        VALID_WEATHER_CONDITION_TIMEFRAME: validWeatherConditionTimeframe,
    };
};
